import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  Loader2, Plus, MessageSquare, Send, Sparkles, BarChart3, Bell,
  Award, Rocket, X, ChevronRight, Download, ExternalLink, AlertTriangle,
  CheckCircle2, ShieldCheck, Star, FileText,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import {
  fbBootstrap, fbSurveys, fbDraft, fbCreateSurvey, fbSurvey,
  fbUpdateSurvey, fbActivateSurvey, fbSurveyLink, fbCreateCampaign,
  fbSendCampaign, fbInsights, fbResponses, fbAggregate, fbAlerts,
  fbTestimonials, fbApproveTestimonial, fbPublishQueue, fbApplyPublish,
  fbDashboard,
} from "@/lib/api";

const field = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500";
const btn = "inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition";
const btnPrimary = `${btn} bg-emerald-600 text-white hover:bg-emerald-500`;
const btnGhost = `${btn} bg-slate-100 text-slate-700 hover:bg-slate-200`;
const STATUS = {
  draft: "bg-slate-100 text-slate-700",
  active: "bg-emerald-100 text-emerald-700",
  paused: "bg-amber-100 text-amber-700",
  closed: "bg-slate-100 text-slate-500",
  archived: "bg-slate-100 text-slate-500",
};

const TABS = [
  { key: "overview",     label: "Overview",     icon: BarChart3 },
  { key: "builder",      label: "Builder",      icon: FileText },
  { key: "distribute",   label: "Distribute",   icon: Send },
  { key: "insights",     label: "Insights",     icon: Sparkles },
  { key: "responses",    label: "Responses",    icon: MessageSquare },
  { key: "testimonials", label: "Testimonials", icon: Award },
  { key: "alerts",       label: "Alerts",       icon: Bell },
  { key: "exports",      label: "Exports",      icon: Download },
];

export default function FeedbackStudio() {
  const { user } = useAuth();
  const { sid } = useParams();
  const navigate = useNavigate();
  const [boot, setBoot] = useState(null);
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("overview");
  const [dashboard, setDashboard] = useState(null);
  const [draftOpen, setDraftOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    const b = await fbBootstrap();
    setBoot(b);
    const s = await fbSurveys();
    setList(s.items || []);
    setDashboard(await fbDashboard());
    setLoading(false);
    if (sid) {
      const found = (s.items || []).find((x) => x.id === sid);
      if (found) setSelected(found);
    } else if ((s.items || []).length > 0 && !selected) {
      setSelected(s.items[0]);
    }
  }, [sid, selected]);

  useEffect(() => { if (user) reload().catch(() => setLoading(false)); }, [user, reload]);

  if (user === undefined) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (user === null) return (
    <div className="min-h-screen flex flex-col"><Navbar />
      <div className="flex-1 grid place-items-center px-6">
        <div className="max-w-md text-center" data-testid="fb-login-gate">
          <MessageSquare className="w-10 h-10 text-emerald-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Customer Voice AI</h1>
          <p className="text-slate-600 mb-6">Sign in to draft and send surveys.</p>
          <Link to="/login" className={btnPrimary}>Sign in</Link>
        </div>
      </div>
      <Footer />
    </div>
  );
  if (loading) return <div className="min-h-screen flex flex-col"><Navbar /><div className="flex-1 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div></div>;

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 pt-24 pb-16" data-testid="feedback-studio">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-emerald-700">
                <MessageSquare className="w-3.5 h-3.5" /> Customer Voice AI
              </div>
              <h1 className="mt-2 text-3xl font-bold text-slate-900">Feedback Assistant</h1>
              <p className="mt-1 text-sm text-slate-500">Turn feedback into measurable business and website improvements.</p>
            </div>
            <button onClick={() => setDraftOpen(true)} className={btnPrimary} data-testid="new-survey-btn"><Plus className="w-4 h-4" /> New survey</button>
          </div>

          {/* Global dashboard tiles */}
          {dashboard && (
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
              {[
                ["Surveys", dashboard.totals.surveys, "tile-surveys"],
                ["Active", dashboard.totals.active_surveys, "tile-active"],
                ["Responses", dashboard.totals.responses, "tile-responses"],
                ["Alerts", dashboard.totals.open_alerts, "tile-alerts"],
                ["Pending testimonials", dashboard.totals.pending_testimonials, "tile-testimonials"],
                ["Pending publish", dashboard.totals.pending_publish, "tile-publish"],
              ].map(([l, v, tid]) => (
                <div key={tid} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={tid}>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{l}</div>
                  <div className="mt-1 text-2xl font-bold text-slate-900">{v ?? 0}</div>
                </div>
              ))}
            </div>
          )}

          <div className="grid lg:grid-cols-[280px_1fr] gap-6">
            <aside className="space-y-2">
              {list.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center" data-testid="empty-surveys">
                  <MessageSquare className="w-10 h-10 mx-auto text-slate-300" />
                  <div className="mt-3 text-sm font-semibold text-slate-900">No surveys yet</div>
                  <button onClick={() => setDraftOpen(true)} className={`${btnPrimary} mt-4`}>Draft your first survey</button>
                </div>
              ) : list.map((s) => (
                <button key={s.id} onClick={() => setSelected(s)} data-testid={`survey-${s.id}`}
                  className={`w-full text-left rounded-xl border p-3 transition ${selected?.id === s.id ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-semibold text-slate-900 truncate">{s.title}</div>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${STATUS[s.status]}`}>{s.status}</span>
                  </div>
                  <div className="mt-1 text-[11px] text-slate-500">{(s.questions || []).length} questions · v{s.version}</div>
                </button>
              ))}
            </aside>

            <section>
              {!selected ? (
                <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center">
                  <div className="text-sm text-slate-500">Select a survey to configure.</div>
                </div>
              ) : (
                <SurveyDetail survey={selected} boot={boot} onReload={reload} onSelect={setSelected} tab={tab} setTab={setTab} />
              )}
            </section>
          </div>
        </div>
      </main>
      {draftOpen && <DraftModal boot={boot} onClose={() => setDraftOpen(false)} onCreated={(s) => { setDraftOpen(false); reload(); setSelected(s); navigate(`/feedback/${s.id}`); }} />}
      <Footer />
    </div>
  );
}

// ---------------------------------------------------------------------------
function DraftModal({ boot, onClose, onCreated }) {
  const [step, setStep] = useState(1); // 1 = describe, 2 = review
  const [request, setRequest] = useState("");
  const [templateKey, setTemplateKey] = useState("");
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const generate = async () => {
    if (!request.trim() && !templateKey) { setErr("Describe what you want to learn, or pick a template."); return; }
    setBusy(true); setErr("");
    try {
      const d = await fbDraft(request || "Create a general customer satisfaction survey.", templateKey || null);
      setDraft(d); setStep(2);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Draft failed");
    } finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const s = await fbCreateSurvey(draft);
      onCreated(s);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 grid place-items-center z-50 p-4" data-testid="draft-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-slate-900">{step === 1 ? "Describe your survey" : "Review AI draft"}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Or start from a template</label>
              <select value={templateKey} onChange={(e) => setTemplateKey(e.target.value)} className={field + " mt-1"} data-testid="draft-template">
                <option value="">— No template —</option>
                {(boot?.templates || []).map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Describe what you want to learn</label>
              <textarea value={request} onChange={(e) => setRequest(e.target.value)} rows={5} className={field + " mt-1"} placeholder="e.g. Create a short survey for patients after a dental appointment. Measure friendliness, waiting time and clarity of treatment explanations. Keep it under two minutes." data-testid="draft-request" />
            </div>
            {err && <div className="rounded-lg border border-rose-200 bg-rose-50 text-rose-700 text-xs p-3" data-testid="draft-error">{err}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className={btnGhost}>Cancel</button>
              <button onClick={generate} disabled={busy} className={btnPrimary} data-testid="draft-generate">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Draft with AI
              </button>
            </div>
          </div>
        )}

        {step === 2 && draft && (
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Title</label>
              <input value={draft.title} onChange={(e) => setDraft({...draft, title: e.target.value})} className={field + " mt-1"} data-testid="draft-title" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Intro</label>
              <textarea value={draft.intro} rows={2} onChange={(e) => setDraft({...draft, intro: e.target.value})} className={field + " mt-1"} data-testid="draft-intro" />
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Questions ({draft.questions.length})</div>
              <ol className="space-y-2 list-decimal list-inside text-sm text-slate-700">
                {draft.questions.map((q, i) => (
                  <li key={i} data-testid={`draft-q-${i}`}>
                    <span className="text-slate-500 text-[11px] font-mono">[{q.type}]</span> {q.text}
                  </li>
                ))}
              </ol>
            </div>
            {draft.quality && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs">
                <div className="font-bold text-emerald-900">Survey quality score: {draft.quality.score}/100</div>
                {draft.quality.issues.length > 0 && (
                  <div className="mt-1 text-emerald-800">Issues: {draft.quality.issues.map((i) => i.kind).join(", ")}</div>
                )}
              </div>
            )}
            {err && <div className="rounded-lg border border-rose-200 bg-rose-50 text-rose-700 text-xs p-3">{err}</div>}
            <div className="flex justify-between gap-2 pt-2">
              <button onClick={() => setStep(1)} className={btnGhost}>← Back</button>
              <button onClick={save} disabled={busy} className={btnPrimary} data-testid="draft-save">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Save as draft
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function SurveyDetail({ survey, boot, onReload, onSelect, tab, setTab }) {
  const [busy, setBusy] = useState(false);

  const activate = async () => {
    setBusy(true);
    try { await fbActivateSurvey(survey.id); onReload(); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="survey-detail">
      <div className="p-6 border-b border-slate-100 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl font-bold text-slate-900">{survey.title}</h2>
            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${STATUS[survey.status]}`}>{survey.status}</span>
            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">v{survey.version}</span>
            {survey.quality_score !== undefined && (
              <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                Quality {survey.quality_score}
              </span>
            )}
          </div>
          <div className="mt-1 text-sm text-slate-500 max-w-2xl">{survey.purpose}</div>
        </div>
        <div className="flex gap-2">
          {survey.status !== "active" && (
            <button onClick={activate} disabled={busy} className={btnPrimary} data-testid="activate-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} Activate
            </button>
          )}
        </div>
      </div>

      <div className="border-b border-slate-100 px-4 flex flex-wrap gap-1 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
            className={`flex items-center gap-1.5 px-3 py-3 text-xs font-semibold border-b-2 transition ${tab === t.key ? "border-emerald-600 text-emerald-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      <div className="p-6">
        {tab === "overview"     && <OverviewTab survey={survey} />}
        {tab === "builder"      && <BuilderTab survey={survey} onReload={onReload} onSelect={onSelect} />}
        {tab === "distribute"   && <DistributeTab survey={survey} onReload={onReload} />}
        {tab === "insights"     && <InsightsTab survey={survey} />}
        {tab === "responses"    && <ResponsesTab survey={survey} />}
        {tab === "testimonials" && <TestimonialsTab onReload={onReload} />}
        {tab === "alerts"       && <AlertsTab />}
        {tab === "exports"      && <ExportsTab survey={survey} />}
      </div>
    </div>
  );
}

function OverviewTab({ survey }) {
  const [ins, setIns] = useState(null);
  const [agg, setAgg] = useState(null);
  useEffect(() => {
    fbInsights(survey.id).then(setIns).catch(() => {});
    fbAggregate(survey.id, 20).then(setAgg).catch(() => {});
  }, [survey.id]);

  return (
    <div className="space-y-5" data-testid="overview-tab">
      {!ins ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tile label="Responses" value={ins.totals.responses} />
            <Tile label="CSAT avg" value={ins.totals.csat_avg ?? "—"} />
            <Tile label="NPS" value={ins.totals.nps ?? "—"} />
            <Tile label="Low scores" value={ins.totals.low_scores} />
          </div>
          {agg?.eligible && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4" data-testid="aggregate-stat">
              <div className="text-xs font-bold uppercase tracking-widest text-emerald-700 mb-1">Publishable aggregate stat</div>
              <div className="text-sm text-slate-800">{agg.statement}</div>
            </div>
          )}
          {agg && !agg.eligible && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="aggregate-not-eligible">
              <AlertTriangle className="w-4 h-4 inline mr-1" /> {agg.reason}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function BuilderTab({ survey, onReload, onSelect }) {
  const [qs, setQs] = useState(survey.questions || []);
  const [meta, setMeta] = useState({
    title: survey.title, intro: survey.intro, purpose: survey.purpose,
    thank_you_message: survey.thank_you_message,
    anonymous_default: survey.anonymous_default,
  });
  const [saving, setSaving] = useState(false);
  useEffect(() => { setQs(survey.questions || []); }, [survey]);

  const updateQ = (i, patch) => setQs(qs.map((q, idx) => idx === i ? { ...q, ...patch } : q));
  const del = (i) => setQs(qs.filter((_, idx) => idx !== i));
  const add = () => setQs([...qs, { id: Math.random().toString(36).slice(2, 10), type: "csat", text: "New question", required: true }]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await fbUpdateSurvey(survey.id, { ...meta, questions: qs });
      onSelect(r); onReload();
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="builder-tab">
      <div className="grid md:grid-cols-2 gap-3">
        <div><label className="text-xs font-semibold text-slate-700 uppercase">Title</label><input value={meta.title} onChange={(e) => setMeta({...meta, title: e.target.value})} className={field} data-testid="b-title" /></div>
        <div><label className="text-xs font-semibold text-slate-700 uppercase">Purpose</label><input value={meta.purpose} onChange={(e) => setMeta({...meta, purpose: e.target.value})} className={field} data-testid="b-purpose" /></div>
        <div className="md:col-span-2"><label className="text-xs font-semibold text-slate-700 uppercase">Intro</label><textarea value={meta.intro} rows={2} onChange={(e) => setMeta({...meta, intro: e.target.value})} className={field} data-testid="b-intro" /></div>
        <div className="md:col-span-2"><label className="text-xs font-semibold text-slate-700 uppercase">Thank you message</label><input value={meta.thank_you_message} onChange={(e) => setMeta({...meta, thank_you_message: e.target.value})} className={field} data-testid="b-thankyou" /></div>
        <div className="md:col-span-2">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!meta.anonymous_default} onChange={(e) => setMeta({...meta, anonymous_default: e.target.checked})} data-testid="b-anonymous" />
            <span className="text-sm text-slate-700">Anonymous by default (recommended)</span>
          </label>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-bold text-slate-900">Questions ({qs.length})</div>
          <button onClick={add} className={btnGhost} data-testid="b-add-q"><Plus className="w-3.5 h-3.5" /> Add</button>
        </div>
        <div className="space-y-2">
          {qs.map((q, i) => (
            <div key={i} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`b-q-${i}`}>
              <div className="flex items-start gap-2">
                <select value={q.type} onChange={(e) => updateQ(i, { type: e.target.value })} className="rounded border border-slate-300 px-2 py-1 text-xs">
                  {["csat", "nps", "ces", "star", "smiley", "yes_no", "single_choice", "multi_choice", "short_text", "long_text", "consent", "date", "number"].map((t) => <option key={t}>{t}</option>)}
                </select>
                <input value={q.text} onChange={(e) => updateQ(i, { text: e.target.value })} className={field + " flex-1"} data-testid={`b-q-text-${i}`} />
                <button onClick={() => del(i)} className="text-slate-400 hover:text-rose-600" data-testid={`b-q-del-${i}`}><X className="w-4 h-4" /></button>
              </div>
              <div className="mt-2 flex items-center gap-3 text-xs text-slate-600">
                <label className="inline-flex items-center gap-1">
                  <input type="checkbox" checked={!!q.required} onChange={(e) => updateQ(i, { required: e.target.checked })} />
                  Required
                </label>
                {(q.type === "single_choice" || q.type === "multi_choice") && (
                  <input value={(q.options || []).join(",")} onChange={(e) => updateQ(i, { options: e.target.value.split(",").map((x) => x.trim()) })} placeholder="Options, comma-separated" className="rounded border border-slate-300 px-2 py-1 text-xs flex-1" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className={btnPrimary} data-testid="b-save"><Send className="w-4 h-4" /> {saving ? "Saving…" : "Save"}</button>
      </div>
    </div>
  );
}

function DistributeTab({ survey, onReload }) {
  const [link, setLink] = useState(null);
  const [audience, setAudience] = useState("");
  const [channel, setChannel] = useState("email");
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState("");
  useEffect(() => { fbSurveyLink(survey.id).then(setLink).catch(() => {}); }, [survey.id]);

  const send = async () => {
    const rows = audience.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
      const parts = l.split(",");
      return { email: parts[0]?.trim(), name: parts[1]?.trim() || null, phone: parts[2]?.trim() || null };
    });
    if (rows.length === 0) return;
    setSending(true); setMsg("");
    try {
      const c = await fbCreateCampaign(survey.id, {
        name: `Campaign ${new Date().toLocaleDateString()}`,
        channel, audience: rows,
      });
      const r = await fbSendCampaign(c.id);
      setMsg(`Sent ${r.sent} invitations.`);
      onReload();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-5" data-testid="distribute-tab">
      {survey.status !== "active" && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          Activate the survey before sending. (Sending will auto-activate a draft.)
        </div>
      )}
      {link && (
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Public link + QR</div>
          <div className="flex items-center gap-2">
            <input value={link.public_link} readOnly className={field} data-testid="public-link" />
            <button onClick={() => navigator.clipboard.writeText(link.public_link)} className={btnGhost}>Copy</button>
            <a href={link.qr_endpoint} target="_blank" rel="noreferrer" className={btnGhost}><ExternalLink className="w-3.5 h-3.5" /> QR</a>
          </div>
          <div className="mt-2 text-[11px] text-slate-500">Anonymous — multiple responses allowed unless embedded via tokenized invite.</div>
        </div>
      )}
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Send to a list</div>
        <div className="grid md:grid-cols-4 gap-2">
          <select value={channel} onChange={(e) => setChannel(e.target.value)} className={field} data-testid="d-channel">
            <option value="email">Email</option>
            <option value="sms">SMS (queued)</option>
            <option value="whatsapp">WhatsApp (queued)</option>
            <option value="link">Link only</option>
          </select>
          <textarea value={audience} onChange={(e) => setAudience(e.target.value)} rows={4} placeholder="email, name, phone (one per line)" className={field + " md:col-span-3"} data-testid="d-audience" />
        </div>
        <div className="mt-2 rounded-lg border border-rose-200 bg-rose-50 p-2 text-[11px] text-rose-800">
          <ShieldCheck className="w-3.5 h-3.5 inline mr-1" /> Review-gating is prohibited on this platform. Invitations must be sent consistently, regardless of expected score.
        </div>
        <div className="flex justify-between items-center mt-3">
          <div className="text-xs text-slate-600">{msg}</div>
          <button onClick={send} disabled={sending} className={btnPrimary} data-testid="d-send"><Send className="w-4 h-4" /> {sending ? "Sending…" : "Send"}</button>
        </div>
      </div>
    </div>
  );
}

function InsightsTab({ survey }) {
  const [ins, setIns] = useState(null);
  useEffect(() => { fbInsights(survey.id).then(setIns).catch(() => {}); }, [survey.id]);
  if (!ins) return <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  return (
    <div className="space-y-5" data-testid="insights-tab">
      <div className="text-xs text-slate-500">{ins.sample_note}</div>
      <div className="space-y-3">
        {ins.insights.map((i, idx) => (
          <div key={idx} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`insight-${i.kind}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-bold uppercase tracking-widest text-emerald-700">{i.kind}</span>
              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${i.confidence === "high" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : i.confidence === "medium" ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>Confidence: {i.confidence}</span>
              <span className="text-[10px] text-slate-500">Supporting: {i.supporting_count}</span>
            </div>
            <div className="text-sm text-slate-800"><b>Measured:</b> {i.measured}</div>
            <div className="mt-1 text-sm text-slate-800"><b>Interpretation:</b> {i.interpretation}</div>
            <div className="mt-1 text-sm text-slate-800"><b>Suggested action:</b> {i.suggested_action}</div>
          </div>
        ))}
        {ins.insights.length === 0 && <div className="text-xs text-slate-500">No responses yet — insights will appear as data comes in.</div>}
      </div>
    </div>
  );
}

function ResponsesTab({ survey }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { fbResponses(survey.id).then((d) => setRows(d.items || [])).catch(() => {}); }, [survey.id]);
  return (
    <div className="space-y-3" data-testid="responses-tab">
      {rows.length === 0 && <div className="text-xs text-slate-500">No responses yet.</div>}
      {rows.slice(0, 50).map((r) => (
        <div key={r.id} className="rounded-lg border border-slate-200 bg-white p-3 text-xs" data-testid={`resp-${r.id}`}>
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-2 py-0.5 rounded-full font-bold text-[10px] ${r.sentiment === "positive" ? "bg-emerald-100 text-emerald-700" : r.sentiment === "negative" ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"}`}>{r.sentiment}</span>
            {r.low_score && <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[10px] font-bold">low score</span>}
            {r.risk_family && <span className="px-2 py-0.5 rounded-full bg-rose-200 text-rose-900 text-[10px] font-bold">risk: {r.risk_family}</span>}
            <span className="text-slate-400">{r.submitted_at?.slice(0, 16)}</span>
          </div>
          <div className="text-slate-700">CSAT: {r.csat ?? "—"} · themes: {(r.themes || []).join(", ") || "—"}</div>
        </div>
      ))}
    </div>
  );
}

function TestimonialsTab({ onReload }) {
  const [items, setItems] = useState([]);
  const [pq, setPq] = useState([]);
  const reload = useCallback(() => {
    fbTestimonials().then((d) => setItems(d.items || [])).catch(() => {});
    fbPublishQueue().then((d) => setPq(d.items || [])).catch(() => {});
  }, []);
  useEffect(() => { reload(); }, [reload]);
  const approve = async (t) => {
    const quote = prompt("Approved quote (you can edit):", t.quote) || t.quote;
    const mode = prompt("Display mode? (full_name / first_last_initial / initials / company / city / anonymous)", "first_last_initial") || "anonymous";
    const name = mode === "anonymous" ? null : (prompt("Attribution name (optional):", "") || null);
    await fbApproveTestimonial(t.id, { display_mode: mode, attribution_name: name, approved_quote: quote });
    reload(); onReload();
  };
  const apply = async (p) => { await fbApplyPublish(p.id); reload(); onReload(); };
  return (
    <div className="space-y-4" data-testid="testimonials-tab">
      <div>
        <div className="text-sm font-bold text-slate-900 mb-2">Pending review ({items.filter((t) => t.status === "pending_review").length})</div>
        <div className="space-y-2">
          {items.filter((t) => t.status === "pending_review").map((t) => (
            <div key={t.id} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`test-${t.id}`}>
              <div className="text-sm text-slate-800">"{t.quote}"</div>
              <div className="mt-2 flex gap-2">
                <button onClick={() => approve(t)} className={btnPrimary}><CheckCircle2 className="w-3.5 h-3.5" /> Approve</button>
              </div>
            </div>
          ))}
          {items.filter((t) => t.status === "pending_review").length === 0 && <div className="text-xs text-slate-500">No pending testimonials.</div>}
        </div>
      </div>
      <div>
        <div className="text-sm font-bold text-slate-900 mb-2">Website publishing queue ({pq.filter((p) => p.status === "awaiting_client_approval").length})</div>
        <div className="space-y-2">
          {pq.filter((p) => p.status === "awaiting_client_approval").map((p) => (
            <div key={p.id} className="rounded-lg border border-slate-200 bg-white p-3 flex items-start justify-between gap-3" data-testid={`pq-${p.id}`}>
              <div className="min-w-0">
                <div className="text-xs font-bold uppercase tracking-widest text-emerald-700">{p.kind}</div>
                <div className="text-sm text-slate-800 mt-1 truncate">"{p.payload?.quote}"</div>
                <div className="text-[11px] text-slate-500 mt-1">Target: {p.target_page}</div>
              </div>
              <button onClick={() => apply(p)} className={btnPrimary}><Rocket className="w-3.5 h-3.5" /> Create draft</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AlertsTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { fbAlerts().then((d) => setItems(d.items || [])).catch(() => {}); }, []);
  return (
    <div className="space-y-2" data-testid="alerts-tab">
      {items.length === 0 && <div className="text-xs text-slate-500">No alerts.</div>}
      {items.map((a) => (
        <div key={a.id} className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs" data-testid={`alert-${a.id}`}>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5 text-rose-600" />
            <span className="font-bold text-rose-900">{a.kind}</span>
            {a.risk_family && <span className="ml-1 rounded-full bg-rose-200 text-rose-900 px-2 py-0.5">risk: {a.risk_family}</span>}
            <span className="ml-auto text-slate-500">{a.at?.slice(0, 16)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function ExportsTab({ survey }) {
  const base = process.env.REACT_APP_BACKEND_URL;
  const withToken = (path) => `${base}${path}`;
  const [downloading, setDownloading] = useState("");
  const download = async (path, filename) => {
    setDownloading(path);
    try {
      const token = localStorage.getItem("wt_token");
      const r = await fetch(withToken(path), { headers: { Authorization: `Bearer ${token}` } });
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } finally { setDownloading(""); }
  };
  const items = [
    ["analysis.csv (PII redacted)", `/api/fb/surveys/${survey.id}/export/analysis.csv`, `analysis-${survey.id}.csv`, "analysis-csv"],
    ["contacts.csv (owner-only PII)", `/api/fb/surveys/${survey.id}/export/contacts.csv`, `contacts-${survey.id}.csv`, "contacts-csv"],
    ["report.xlsx (Excel workbook)", `/api/fb/surveys/${survey.id}/export/report.xlsx`, `report-${survey.id}.xlsx`, "report-xlsx"],
    ["report.pdf (executive PDF)", `/api/fb/surveys/${survey.id}/export/report.pdf`, `report-${survey.id}.pdf`, "report-pdf"],
  ];
  return (
    <div className="space-y-2" data-testid="exports-tab">
      {items.map(([label, path, filename, tid]) => (
        <div key={tid} className="rounded-lg border border-slate-200 bg-white p-3 flex items-center justify-between" data-testid={`export-${tid}`}>
          <div className="text-sm text-slate-800">{label}</div>
          <button onClick={() => download(path, filename)} disabled={downloading === path} className={btnPrimary}>
            {downloading === path ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Download
          </button>
        </div>
      ))}
    </div>
  );
}

function Tile({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-bold text-slate-900">{value ?? 0}</div>
    </div>
  );
}
